/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useRef, useState, useEffect } from 'react';

import useOnClickOutside from '../../../hooks/useOnClickOutside';
import classes from './styles.module.scss';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';

const EditableTitle = ({
  children: title,
  handleUpdateTitle,
  isEditable,
  isEditing,
  setIsEditing,
  className,
  clickTitleToEdit = false,
}) => {
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [newTitle, setNewTitle] = useState(title);
  const [isEditButtonVisible, setIsEditButtonVisible] = useState(false);

  const inputRef = useRef(null);

  const isMobile = useCheckMobileScreen();

  const handleSubmit = () => {
    const trimmed = newTitle.trim();
    if (!trimmed) {
      setNewTitle(title);
      setIsEditing?.(false);
      setIsEditingLocal(false);
      return;
    }
    if (trimmed !== title) {
      handleUpdateTitle(trimmed);
    } else {
      setIsEditing?.(false);
      setIsEditingLocal(false);
    }
    // Note: Editing state should be reset in mutation callbacks (onSuccess/onError)
    // This allows error handling to work properly
  };

  useOnClickOutside(inputRef, () => handleSubmit());

  // Keep newTitle in sync with title when in view mode (so we show correct value when entering edit)
  useEffect(() => {
    if (!isEditing && !isEditingLocal) {
      setNewTitle(title);
    }
  }, [title, isEditing, isEditingLocal]);

  // Focus input when editing
  useEffect(() => {
    if (isEditing || isEditingLocal) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 0);
    }
  }, [isEditing, isEditingLocal]);

  const handleClickTitle = (e) => {
    if (!isEditable) {
      return;
    }

    e.stopPropagation();

    if (!newTitle.trim().length) {
      setNewTitle(title);
    }

    if (setIsEditing) {
      setIsEditing(true);
    } else {
      setIsEditingLocal(true);
    }

    setTimeout(() => {
      inputRef.current.focus();
    }, 0);
  };

  const handleChangeTitle = (event) => {
    setNewTitle(event.target.value);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div
      onMouseOver={() => setIsEditButtonVisible(true)}
      onMouseLeave={() => setIsEditButtonVisible(false)}
      className={className ? `${classes.EditableTitle} ${className}` : classes.EditableTitle}
    >
      {isEditing || isEditingLocal ? (
        <input
          ref={inputRef}
          value={newTitle}
          onChange={handleChangeTitle}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyPress}
          type="text"
        />
      ) : (
        <>
          <span
            onClick={clickTitleToEdit && isEditable ? handleClickTitle : undefined}
            role={clickTitleToEdit && isEditable ? 'button' : undefined}
            tabIndex={clickTitleToEdit && isEditable ? 0 : undefined}
            onKeyDown={
              clickTitleToEdit && isEditable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClickTitle(e);
                    }
                  }
                : undefined
            }
          >
            {title}
          </span>
          {isEditable && isEditButtonVisible && !isMobile && (
            <button
              onClick={handleClickTitle}
              type="button"
              className={classes.editButton}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EditableTitle;
